import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  GiftIcon,
  DocumentIcon,
  ShoppingCartIcon,
  KeyIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';

type DataType = 'todos' | 'gifts' | 'documents' | 'shopping' | 'passwords' | 'calendar';

interface DataStats {
  todo_boards: number;
  todo_items: number;
  gift_lists: number;
  gifts: number;
  documents: number;
  shopping_lists: number;
  shopping_items: number;
  passwords: number;
  calendar_events: number;
}

const dataTypes: { id: DataType; label: string; icon: React.ComponentType<{ className?: string }>; color: string }[] = [
  { id: 'todos', label: 'Todo Boards', icon: ClipboardDocumentListIcon, color: 'blue' },
  { id: 'gifts', label: 'Gift Lists', icon: GiftIcon, color: 'pink' },
  { id: 'documents', label: 'Documents', icon: DocumentIcon, color: 'amber' },
  { id: 'shopping', label: 'Shopping Lists', icon: ShoppingCartIcon, color: 'green' },
  { id: 'passwords', label: 'Passwords', icon: KeyIcon, color: 'purple' },
  { id: 'calendar', label: 'Calendar Events', icon: CalendarIcon, color: 'red' },
];

export default function AdminData() {
  const [activeType, setActiveType] = useState<DataType>('todos');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [detailModal, setDetailModal] = useState<{ type: string; id: string; name: string } | null>(null);

  const { data: stats } = useQuery({
    queryKey: ['admin', 'data', 'stats'],
    queryFn: async () => {
      const response = await api.get('/admin/data/stats');
      return response.data as DataStats;
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'data', activeType, page, search],
    queryFn: async () => {
      const params: Record<string, any> = { page, per_page: 30 };
      if (search) params.search = search;
      const response = await api.get(`/admin/data/${activeType}`, { params });
      return response.data;
    },
  });

  const { data: detailItems } = useQuery({
    queryKey: ['admin', 'data', 'detail', detailModal?.type, detailModal?.id],
    queryFn: async () => {
      if (!detailModal) return null;
      const response = await api.get(`/admin/data/${detailModal.type}/${detailModal.id}/items`);
      return response.data.items;
    },
    enabled: !!detailModal,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const getStatCount = (type: DataType) => {
    if (!stats) return 0;
    switch (type) {
      case 'todos': return stats.todo_boards;
      case 'gifts': return stats.gift_lists;
      case 'documents': return stats.documents;
      case 'shopping': return stats.shopping_lists;
      case 'passwords': return stats.passwords;
      case 'calendar': return stats.calendar_events;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    pink: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    green: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Data Browser</h1>
        <p className="text-surface-400 mt-1">View all user data across all families</p>
      </div>

      {/* Data Type Tabs */}
      <div className="flex flex-wrap gap-2">
        {dataTypes.map((type) => {
          const isActive = activeType === type.id;
          const count = getStatCount(type.id);
          return (
            <button
              key={type.id}
              onClick={() => {
                setActiveType(type.id);
                setPage(1);
                setSearch('');
                setSearchInput('');
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                isActive
                  ? colorClasses[type.color]
                  : 'bg-surface-800/50 text-surface-400 border-surface-700 hover:bg-surface-700/50 hover:text-white'
              }`}
            >
              <type.icon className="w-5 h-5" />
              <span className="font-medium">{type.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                isActive ? 'bg-white/20' : 'bg-surface-600'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-surface-800 border border-surface-600 text-white placeholder-surface-400 focus:outline-none focus:border-amber-500"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 rounded-lg bg-surface-700 hover:bg-surface-600 text-white transition-colors"
        >
          Search
        </button>
      </form>

      {/* Data Table */}
      <div className="bg-surface-800/50 rounded-xl border border-surface-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-700/50">
              <tr>
                {activeType === 'todos' && (
                  <>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Board Name</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Family</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Items</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Created</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-surface-300">Actions</th>
                  </>
                )}
                {activeType === 'gifts' && (
                  <>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">List Name</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Family</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Owner</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Gifts</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Created</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-surface-300">Actions</th>
                  </>
                )}
                {activeType === 'documents' && (
                  <>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Filename</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Family</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Uploaded By</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Size</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Created</th>
                  </>
                )}
                {activeType === 'shopping' && (
                  <>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">List Name</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Family</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Created By</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Items</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Created</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-surface-300">Actions</th>
                  </>
                )}
                {activeType === 'passwords' && (
                  <>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Name</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Username</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">URL</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Family</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Created By</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Created</th>
                  </>
                )}
                {activeType === 'calendar' && (
                  <>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Event Name</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Family</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Start</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">End</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-surface-300">Created By</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-700">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
                  </td>
                </tr>
              ) : data?.items?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-surface-400">
                    No data found
                  </td>
                </tr>
              ) : (
                data?.items?.map((item: any) => (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-surface-700/30 transition-colors"
                  >
                    {activeType === 'todos' && (
                      <>
                        <td className="px-4 py-3 text-white font-medium">{item.name}</td>
                        <td className="px-4 py-3 text-surface-300">{item.family_name}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 text-xs">
                            {item.item_count} items
                          </span>
                        </td>
                        <td className="px-4 py-3 text-surface-400 text-sm">{formatDate(item.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setDetailModal({ type: 'todos', id: item.id, name: item.name })}
                            className="p-1.5 rounded-lg text-surface-400 hover:text-white hover:bg-surface-600 transition-colors"
                            title="View items"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </>
                    )}
                    {activeType === 'gifts' && (
                      <>
                        <td className="px-4 py-3 text-white font-medium">{item.name}</td>
                        <td className="px-4 py-3 text-surface-300">{item.family_name}</td>
                        <td className="px-4 py-3 text-surface-300">{item.owner_username || '-'}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded bg-pink-500/20 text-pink-400 text-xs">
                            {item.gift_count} gifts
                          </span>
                        </td>
                        <td className="px-4 py-3 text-surface-400 text-sm">{formatDate(item.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setDetailModal({ type: 'gifts', id: item.id, name: item.name })}
                            className="p-1.5 rounded-lg text-surface-400 hover:text-white hover:bg-surface-600 transition-colors"
                            title="View items"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </>
                    )}
                    {activeType === 'documents' && (
                      <>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-white font-medium">{item.title || item.original_filename}</p>
                            {item.title && (
                              <p className="text-xs text-surface-500">{item.original_filename}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-surface-300">{item.family_name}</td>
                        <td className="px-4 py-3 text-surface-300">{item.uploaded_by || '-'}</td>
                        <td className="px-4 py-3 text-surface-400 text-sm">
                          {item.file_size ? `${(item.file_size / 1024).toFixed(1)} KB` : '-'}
                        </td>
                        <td className="px-4 py-3 text-surface-400 text-sm">{formatDate(item.created_at)}</td>
                      </>
                    )}
                    {activeType === 'shopping' && (
                      <>
                        <td className="px-4 py-3 text-white font-medium">{item.name}</td>
                        <td className="px-4 py-3 text-surface-300">{item.family_name}</td>
                        <td className="px-4 py-3 text-surface-300">{item.created_by || '-'}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                            {item.item_count} items
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            item.is_completed 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {item.is_completed ? 'Completed' : 'Active'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-surface-400 text-sm">{formatDate(item.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setDetailModal({ type: 'shopping', id: item.id, name: item.name })}
                            className="p-1.5 rounded-lg text-surface-400 hover:text-white hover:bg-surface-600 transition-colors"
                            title="View items"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </>
                    )}
                    {activeType === 'passwords' && (
                      <>
                        <td className="px-4 py-3 text-white font-medium">{item.name}</td>
                        <td className="px-4 py-3 text-surface-300">{item.username || '-'}</td>
                        <td className="px-4 py-3 text-surface-400 text-sm truncate max-w-[200px]">
                          {item.url || '-'}
                        </td>
                        <td className="px-4 py-3 text-surface-300">{item.family_name}</td>
                        <td className="px-4 py-3 text-surface-300">{item.created_by || '-'}</td>
                        <td className="px-4 py-3 text-surface-400 text-sm">{formatDate(item.created_at)}</td>
                      </>
                    )}
                    {activeType === 'calendar' && (
                      <>
                        <td className="px-4 py-3 text-white font-medium">{item.name}</td>
                        <td className="px-4 py-3 text-surface-300">{item.family_name}</td>
                        <td className="px-4 py-3 text-surface-400 text-sm">{formatDateTime(item.start_datetime)}</td>
                        <td className="px-4 py-3 text-surface-400 text-sm">{formatDateTime(item.end_datetime)}</td>
                        <td className="px-4 py-3 text-surface-300">{item.created_by || '-'}</td>
                      </>
                    )}
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-surface-700">
            <p className="text-sm text-surface-400">
              Page {data.page} of {data.pages} ({data.total} total)
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                disabled={page === data.pages}
                className="p-2 rounded-lg hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {detailModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
            onClick={() => setDetailModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-surface-800 rounded-xl p-6 max-w-2xl w-full border border-surface-700 max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">{detailModal.name}</h2>
                <button
                  onClick={() => setDetailModal(null)}
                  className="p-1 rounded-lg hover:bg-surface-700 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5 text-surface-400" />
                </button>
              </div>

              {!detailItems ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
                </div>
              ) : detailItems.length === 0 ? (
                <p className="text-center text-surface-400 py-8">No items found</p>
              ) : (
                <div className="space-y-2">
                  {detailModal.type === 'todos' && detailItems.map((item: any) => (
                    <div key={item.id} className="p-3 rounded-lg bg-surface-700/50">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-white">{item.title}</span>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            item.status === 'done' ? 'bg-emerald-500/20 text-emerald-400' :
                            item.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-surface-600 text-surface-300'
                          }`}>
                            {item.status}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            item.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                            item.priority === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-surface-600 text-surface-300'
                          }`}>
                            {item.priority}
                          </span>
                        </div>
                      </div>
                      {item.description && (
                        <p className="text-sm text-surface-400 mt-1">{item.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-surface-500">
                        {item.assigned_to && <span>Assigned to: {item.assigned_to}</span>}
                        {item.due_date && <span>Due: {formatDate(item.due_date)}</span>}
                      </div>
                    </div>
                  ))}
                  {detailModal.type === 'gifts' && detailItems.map((item: any) => (
                    <div key={item.id} className="p-3 rounded-lg bg-surface-700/50">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-white">{item.name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          item.status === 'purchased' ? 'bg-emerald-500/20 text-emerald-400' :
                          item.status === 'reserved' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-surface-600 text-surface-300'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                      {item.description && (
                        <p className="text-sm text-surface-400 mt-1">{item.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-surface-500">
                        {item.price && <span>Price: ${item.price}</span>}
                        {item.url && <span className="truncate max-w-[200px]">{item.url}</span>}
                      </div>
                    </div>
                  ))}
                  {detailModal.type === 'shopping' && detailItems.map((item: any) => (
                    <div key={item.id} className="p-3 rounded-lg bg-surface-700/50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          item.is_checked ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-surface-500'
                        }`}>
                          {item.is_checked && '✓'}
                        </span>
                        <span className={`font-medium ${item.is_checked ? 'text-surface-400 line-through' : 'text-white'}`}>
                          {item.name}
                        </span>
                      </div>
                      <span className="text-surface-400 text-sm">
                        {item.quantity} {item.unit || ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

